import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tenantApi, Group, Role, RolePermissionsResponse, GroupRolesResponse, User, GroupMemberUser } from '../../services/tenantApi';

const DEFAULT_ROLE_NAMES = new Set(['Viewer', 'Responder', 'TeamLead', 'TenantAdmin']);
const SECURITY_GROUP_TYPE = 'Security';
const PERMISSION_AREA_ORDER = [
  'Alerts',
  'On-Call & Escalation',
  'Automation & Rules',
  'Integrations & Channels',
  'Users & Tenant Settings',
  'Routing & Delivery',
  'Other',
];

const PERMISSION_AREA_BY_NAME: Record<string, string> = {
  ViewAlerts: 'Alerts',
  ChangePriority: 'Alerts',
  ManageSchedules: 'On-Call & Escalation',
  ManageEscalationPolicies: 'On-Call & Escalation',
  ManageRules: 'Automation & Rules',
  ManageWorkflows: 'Automation & Rules',
  ManageIntegrations: 'Integrations & Channels',
  ManageOutboundIntegrations: 'Integrations & Channels',
  ManageNotificationChannels: 'Integrations & Channels',
  ManageUsers: 'Users & Tenant Settings',
  ManageRoles: 'Users & Tenant Settings',
  ManageRoutingRules: 'Routing & Delivery',
};

type RolePermissionItem = {
  permissionId: number;
  name: string;
  description?: string;
  assigned: boolean;
};

type RoleDraft = {
  name: string;
  description: string;
};

export const RolesManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [securityGroupSources, setSecurityGroupSources] = useState<Record<string, string[]>>({});
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateSecurityGroup, setShowCreateSecurityGroup] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [editingSecurityGroup, setEditingSecurityGroup] = useState<Group | null>(null);
  const [permissionsRole, setPermissionsRole] = useState<Role | null>(null);
  const [groupMapRole, setGroupMapRole] = useState<Role | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, groupsData, usersData] = await Promise.all([
        tenantApi.getRoles(),
        tenantApi.getGroups(),
        tenantApi.getUsers({ take: 1000 }),
      ]);
      setRoles(rolesData || []);
      setGroups(groupsData || []);
      const allUsers = Array.isArray((usersData as any)?.users)
        ? (usersData as any).users
        : Array.isArray(usersData)
          ? (usersData as any)
          : [];
      setUsers(allUsers);

      const security = (groupsData || []).filter((group) => (group.type || '').toLowerCase() === SECURITY_GROUP_TYPE.toLowerCase());
      if (security.length > 0) {
        const sourceResponses = await Promise.all(
          security.map(async (group) => {
            try {
              const result = await tenantApi.getGroupSourceGroups(group.groupId);
              return [group.groupId, result.sourceGroupIds || []] as const;
            } catch {
              return [group.groupId, [] as string[]] as const;
            }
          }),
        );
        setSecurityGroupSources(Object.fromEntries(sourceResponses));
      } else {
        setSecurityGroupSources({});
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load roles and groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteRole = async (role: Role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await tenantApi.deleteRole(role.roleId);
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete role');
    }
  };

  const nonDefaultRoles = useMemo(() => roles.filter((r) => !DEFAULT_ROLE_NAMES.has(r.name)), [roles]);
  const securityGroups = useMemo(
    () => groups.filter((group) => (group.type || '').toLowerCase() === SECURITY_GROUP_TYPE.toLowerCase()),
    [groups],
  );
  const regularGroups = useMemo(
    () => groups.filter((group) => (group.type || '').toLowerCase() !== SECURITY_GROUP_TYPE.toLowerCase()),
    [groups],
  );

  const handleDeleteSecurityGroup = async (group: Group) => {
    if (!window.confirm(`Delete security group "${group.name}"?`)) return;
    try {
      await tenantApi.deleteGroup(group.groupId);
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete security group');
    }
  };

  const handleCreateSecurityGroup = async (data: { name: string; memberUserIds: string[]; sourceGroupIds: string[] }) => {
    const created = await tenantApi.createGroup({ name: data.name, type: SECURITY_GROUP_TYPE });
    for (const userId of data.memberUserIds) {
      await tenantApi.addGroupMember(created.groupId, userId);
    }
    await tenantApi.updateGroupSourceGroups(created.groupId, data.sourceGroupIds || []);
    setShowCreateSecurityGroup(false);
    await loadData();
  };

  const handleUpdateSecurityGroup = async (groupId: string, data: { name: string; memberUserIds: string[]; sourceGroupIds: string[] }) => {
    await tenantApi.updateGroup(groupId, { name: data.name, type: SECURITY_GROUP_TYPE });
    const existingMembers = await tenantApi.getGroupMembers(groupId);
    const existingSet = new Set(existingMembers.map((m) => m.userId));
    const nextSet = new Set(data.memberUserIds);

    for (const userId of data.memberUserIds) {
      if (!existingSet.has(userId)) {
        await tenantApi.addGroupMember(groupId, userId);
      }
    }

    for (const member of existingMembers) {
      if (!nextSet.has(member.userId)) {
        await tenantApi.removeGroupMember(groupId, member.userId);
      }
    }

    await tenantApi.updateGroupSourceGroups(groupId, data.sourceGroupIds || []);

    setEditingSecurityGroup(null);
    await loadData();
  };

  return (
    <div className="management-section roles-management-view">
      <div className="roles-subcard">
        <div className="section-header">
          <h3 style={{ margin: 0 }}>RBAC Security Groups ({securityGroups.length})</h3>
          <button className="btn-primary" onClick={() => setShowCreateSecurityGroup(true)}>+ Add Security Group</button>
        </div>

        <table className="data-table roles-table">
          <thead>
            <tr>
              <th>Group</th>
              <th>Source Groups</th>
              <th>Members</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {securityGroups.map((group) => (
              <tr key={group.groupId}>
                <td>{group.name}</td>
                <td>
                  {(securityGroupSources[group.groupId] || [])
                    .map((sourceId) => groups.find((g) => g.groupId === sourceId)?.name)
                    .filter(Boolean)
                    .join(', ') || '-'}
                </td>
                <td>{group.memberCount || 0}</td>
                <td>
                  <div className="roles-actions-row">
                    <button className="btn-icon users-action-btn" title="Edit group" onClick={() => setEditingSecurityGroup(group)}>✏️</button>
                    <button className="btn-icon users-action-btn" title="Delete group" onClick={() => handleDeleteSecurityGroup(group)}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
            {securityGroups.length === 0 && (
              <tr>
                <td colSpan={4} className="empty-row">No security groups found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="roles-subcard" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h3 style={{ margin: 0 }}>Roles & Permissions ({roles.length})</h3>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Add Role</button>
        </div>

        {loading ? (
          <div className="loading">Loading roles...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <table className="data-table roles-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => {
                const isDefault = DEFAULT_ROLE_NAMES.has(role.name);
                return (
                  <tr key={role.roleId}>
                    <td>
                      {role.name}
                      {isDefault && <span className="help-text" style={{ marginLeft: '0.5rem' }}>(default)</span>}
                    </td>
                    <td>{role.description || '-'}</td>
                    <td>
                      <div className="roles-actions-row">
                        <button className="btn-icon users-action-btn" title="Edit role" onClick={() => setEditingRole(role)}>✏️</button>
                        <button className="btn-icon users-action-btn" title="Role permissions" onClick={() => setPermissionsRole(role)}>🔐</button>
                        <button className="btn-icon users-action-btn" title="Group mapping" onClick={() => setGroupMapRole(role)}>👥</button>
                        {!isDefault && (
                          <button className="btn-icon users-action-btn" title="Delete role" onClick={() => handleDeleteRole(role)}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {roles.length === 0 && (
                <tr>
                  <td colSpan={3} className="empty-row">No roles found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div className="help-text" style={{ marginTop: '0.75rem' }}>
          Custom roles: {nonDefaultRoles.length}. Role mapping supports all tenant groups.
        </div>
      </div>

      {showCreate && (
        <RoleEditorModal
          title="Create Role"
          onClose={() => setShowCreate(false)}
          onSave={async (draft) => {
            await tenantApi.createRole(draft);
            setShowCreate(false);
            await loadData();
          }}
        />
      )}

      {editingRole && (
        <RoleEditorModal
          title={`Edit Role: ${editingRole.name}`}
          initial={{ name: editingRole.name, description: editingRole.description || '' }}
          onClose={() => setEditingRole(null)}
          onSave={async (draft) => {
            await tenantApi.updateRole(editingRole.roleId, draft);
            setEditingRole(null);
            await loadData();
          }}
        />
      )}

      {permissionsRole && (
        <RolePermissionsModal
          role={permissionsRole}
          onClose={() => setPermissionsRole(null)}
          onSaved={loadData}
        />
      )}

      {groupMapRole && (
        <GroupRoleMappingModal
          role={groupMapRole}
          groups={groups}
          onClose={() => setGroupMapRole(null)}
          onSaved={loadData}
        />
      )}

      {showCreateSecurityGroup && (
        <SecurityGroupModal
          users={users}
          availableGroups={regularGroups}
          onClose={() => setShowCreateSecurityGroup(false)}
          onSave={handleCreateSecurityGroup}
        />
      )}

      {editingSecurityGroup && (
        <SecurityGroupModal
          group={editingSecurityGroup}
          users={users}
          availableGroups={regularGroups}
          onClose={() => setEditingSecurityGroup(null)}
          onSave={(data) => handleUpdateSecurityGroup(editingSecurityGroup.groupId, data)}
        />
      )}
    </div>
  );
};

const SecurityGroupModal: React.FC<{
  group?: Group;
  users: User[];
  availableGroups: Group[];
  onClose: () => void;
  onSave: (data: { name: string; memberUserIds: string[]; sourceGroupIds: string[] }) => Promise<void>;
}> = ({ group, users, availableGroups, onClose, onSave }) => {
  const [name, setName] = useState(group?.name || '');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(!!group);
  const [addingGroups, setAddingGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    const loadMembers = async () => {
      if (!group) {
        setLoadingMembers(false);
        return;
      }
      setLoadingMembers(true);
      try {
        const [members, sourceGroups] = await Promise.all([
          tenantApi.getGroupMembers(group.groupId),
          tenantApi.getGroupSourceGroups(group.groupId).catch(() => ({ sourceGroupIds: [] as string[] })),
        ]);
        if (active) {
          setSelectedUserIds((members || []).map((m: GroupMemberUser) => m.userId));
          setSelectedGroupIds(sourceGroups.sourceGroupIds || []);
        }
      } finally {
        if (active) setLoadingMembers(false);
      }
    };
    loadMembers();
    return () => {
      active = false;
    };
  }, [group]);

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  };

  const toggleSourceGroup = (groupId: string) => {
    setSelectedGroupIds((prev) => prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]);
  };

  const addMembersFromGroups = async () => {
    if (selectedGroupIds.length === 0) {
      return;
    }

    setAddingGroups(true);
    try {
      const memberLists = await Promise.all(selectedGroupIds.map((groupId) => tenantApi.getGroupMembers(groupId)));
      const incomingUserIds = memberLists.flatMap((members) => (members || []).map((m) => m.userId));
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...incomingUserIds])));
    } catch (err: any) {
      alert(err?.message || 'Failed to load selected group members');
    } finally {
      setAddingGroups(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSave({ name: name.trim(), memberUserIds: selectedUserIds, sourceGroupIds: selectedGroupIds });
    } catch (err: any) {
      alert(err?.message || 'Failed to save security group');
      setSubmitting(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (user.displayName || '').toLowerCase().includes(term)
      || (user.email || '').toLowerCase().includes(term);
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>{group ? 'Edit Security Group' : 'Create Security Group'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Include Existing User Groups</label>
            {availableGroups.length === 0 ? (
              <div className="help-text">No regular groups are available yet.</div>
            ) : (
              <>
                <div style={{ border: '1px solid var(--border-color, #ddd)', borderRadius: 6, maxHeight: 180, overflowY: 'auto', padding: '0.5rem', background: 'var(--card-bg)' }}>
                  {availableGroups.map((availableGroup) => (
                    <label key={availableGroup.groupId} className="checkbox-label" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem', background: 'var(--card-bg)' }}>
                      <span>
                        <input
                          type="checkbox"
                          checked={selectedGroupIds.includes(availableGroup.groupId)}
                          onChange={() => toggleSourceGroup(availableGroup.groupId)}
                        />{' '}
                        {availableGroup.name}
                      </span>
                      <span className="help-text">{availableGroup.type}</span>
                    </label>
                  ))}
                </div>
                <div className="form-actions" style={{ marginTop: '0.6rem', borderTop: 'none', paddingTop: 0 }}>
                  <button type="button" className="btn-secondary" onClick={addMembersFromGroups} disabled={addingGroups || selectedGroupIds.length === 0}>
                    {addingGroups ? 'Adding Members...' : 'Add Members from Selected Groups'}
                  </button>
                </div>
              </>
            )}
            <div className="help-text" style={{ marginTop: '0.35rem' }}>
              This adds current members from selected user groups into this Security group.
            </div>
          </div>
          <div className="form-group">
            <label>Members</label>
            {loadingMembers ? (
              <div className="loading" style={{ margin: '0.5rem 0' }}>Loading members...</div>
            ) : (
              <>
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search users by name or email"
                  style={{ marginBottom: '0.5rem' }}
                />
                <div style={{ border: '1px solid var(--border-color, #ddd)', borderRadius: 6, maxHeight: 260, overflowY: 'auto', padding: '0.5rem', background: 'var(--card-bg)' }}>
                  {filteredUsers.length === 0 ? (
                    <div className="help-text">No users available.</div>
                  ) : (
                    filteredUsers.map((user) => (
                      <label key={user.userId} className="checkbox-label" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', background: 'var(--card-bg)' }}>
                        <span>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user.userId)}
                            onChange={() => toggleUser(user.userId)}
                          />{' '}
                          {user.displayName || user.email}
                        </span>
                        <span className="help-text">{user.email}</span>
                      </label>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || loadingMembers}>{submitting ? 'Saving...' : 'Save Group'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RoleEditorModal: React.FC<{
  title: string;
  initial?: RoleDraft;
  onClose: () => void;
  onSave: (draft: RoleDraft) => Promise<void>;
}> = ({ title, initial, onClose, onSave }) => {
  const [draft, setDraft] = useState<RoleDraft>(initial || { name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      setError('Role name is required.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSave({ name: draft.name.trim(), description: draft.description.trim() });
    } catch (err: any) {
      setError(err?.message || 'Failed to save role');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label>Name *</label>
            <input type="text" value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={draft.description} onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))} rows={3} />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RolePermissionsModal: React.FC<{
  role: Role;
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ role, onClose, onSaved }) => {
  const [state, setState] = useState<RolePermissionsResponse & { permissions: RolePermissionItem[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isTenantAdminRole = role.name.trim().toLowerCase() === 'tenantadmin';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tenantApi.getRolePermissions(role.roleId);
      const permissions: RolePermissionItem[] = Array.isArray((data as any)?.permissions)
        ? (data as any).permissions
            .filter((p: any) => Number.isFinite(Number(p?.permissionId)) && typeof p?.name === 'string')
            .map((p: any) => ({
              permissionId: Number(p.permissionId),
              name: p.name,
              description: typeof p.description === 'string' ? p.description : undefined,
              assigned: !!p.assigned,
            }))
        : [];
      setState({ ...(data as any), permissions });
    } catch (err: any) {
      setError(err?.message || 'Failed to load role permissions');
    } finally {
      setLoading(false);
    }
  }, [role.roleId]);

  useEffect(() => {
    load();
  }, [load]);

  const groupedPermissions = useMemo(() => {
    const groups: Record<string, RolePermissionItem[]> = {};
    const permissions = state?.permissions ?? [];

    permissions.forEach((permission) => {
      const area = PERMISSION_AREA_BY_NAME[permission.name] || 'Other';
      if (!groups[area]) {
        groups[area] = [];
      }
      groups[area].push(permission);
    });

    return PERMISSION_AREA_ORDER
      .filter((area) => (groups[area]?.length || 0) > 0)
      .map((area) => ({
        area,
        permissions: groups[area].slice().sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [state]);

  const togglePermission = (permissionId: number) => {
    setState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: prev.permissions.map((p) => p.permissionId === permissionId ? { ...p, assigned: !p.assigned } : p),
      };
    });
  };

  const selectAllInArea = (permissionIds: number[]) => {
    if (!permissionIds.length) return;

    const idSet = new Set(permissionIds);
    setState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: prev.permissions.map((p) => idSet.has(p.permissionId) ? { ...p, assigned: true } : p),
      };
    });
  };

  const handleSave = async () => {
    if (!state) return;
    setSaving(true);
    setError(null);
    try {
      const permissions = Array.isArray(state.permissions) ? state.permissions : [];
      const ids = permissions.filter((p) => p.assigned).map((p) => p.permissionId);
      await tenantApi.updateRolePermissions(role.roleId, ids);
      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to update permissions');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg modal-role-permissions" onClick={(e) => e.stopPropagation()}>
        <h3>Permissions: {role.name}</h3>
        {loading ? (
          <div className="loading">Loading permissions...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            {isTenantAdminRole && (
              <div className="info-message" style={{ marginBottom: '0.75rem' }}>
                Tenant Admin is an implied allow-all role and is read-only. If you need an admin with scoped access, create a custom admin role and assign only the required permissions.
              </div>
            )}
            <div className="permissions-scroll-panel">
              {groupedPermissions.length === 0 ? (
                <div className="help-text">No permission definitions are available for this tenant yet.</div>
              ) : (
                groupedPermissions.map((group) => (
                  <div key={group.area} className="permissions-area-group">
                    <div className="permissions-area-header">
                      <div className="permissions-area-title">{group.area}</div>
                      <button
                        type="button"
                        className="btn-link permissions-select-all-btn"
                        onClick={() => selectAllInArea(group.permissions.map((permission) => permission.permissionId))}
                        disabled={isTenantAdminRole || group.permissions.every((permission) => permission.assigned)}
                      >
                        Select all
                      </button>
                    </div>
                    {group.permissions.map((permission) => (
                      <label key={permission.permissionId} className="checkbox-label" style={{ marginBottom: '0.45rem' }}>
                        <input
                          type="checkbox"
                          checked={isTenantAdminRole ? true : permission.assigned}
                          onChange={() => togglePermission(permission.permissionId)}
                          disabled={isTenantAdminRole}
                        />
                        <strong>{permission.name}</strong>
                        {permission.description && <span>{permission.description}</span>}
                      </label>
                    ))}
                  </div>
                ))
              )}
            </div>
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              {!isTenantAdminRole && (
                <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Permissions'}</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const GroupRoleMappingModal: React.FC<{
  role: Role;
  groups: Group[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ role, groups, onClose, onSaved }) => {
  const [groupMap, setGroupMap] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const responses = await Promise.all(groups.map((g) => tenantApi.getGroupRoles(g.groupId)));
      const map: Record<string, boolean> = {};
      responses.forEach((resp: GroupRolesResponse) => {
        const roles = Array.isArray((resp as any)?.roles) ? (resp as any).roles : [];
        const groupId = (resp as any)?.group?.groupId;
        if (groupId) {
          const assigned = roles.some((r: any) => r.roleId === role.roleId && r.assigned);
          map[groupId] = assigned;
        }
      });
      setGroupMap(map);
    } catch (err: any) {
      setError(err?.message || 'Failed to load group mappings');
    } finally {
      setLoading(false);
    }
  }, [groups, role.roleId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (groupId: string) => {
    setGroupMap((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const group of groups) {
        const response = await tenantApi.getGroupRoles(group.groupId);
        const responseRoles = Array.isArray((response as any)?.roles) ? (response as any).roles : [];
        const roleIds: string[] = responseRoles.filter((r: any) => r.assigned).map((r: any) => r.roleId);
        const hasRole = roleIds.includes(role.roleId);
        const shouldHaveRole = !!groupMap[group.groupId];

        let nextRoleIds: string[] = roleIds;
        if (shouldHaveRole && !hasRole) {
          nextRoleIds = [...roleIds, role.roleId];
        }
        if (!shouldHaveRole && hasRole) {
          nextRoleIds = roleIds.filter((id) => id !== role.roleId);
        }

        if (nextRoleIds.length !== roleIds.length || nextRoleIds.some((id, idx) => id !== roleIds[idx])) {
          await tenantApi.updateGroupRoles(group.groupId, nextRoleIds);
        }
      }

      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save group mappings');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Group Mapping: {role.name}</h3>
        {loading ? (
          <div className="loading">Loading groups...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : groups.length === 0 ? (
          <div className="help-text">No mappable groups found. Create Security, On-Call, Responder Team, or Platform groups first.</div>
        ) : (
          <>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {groups.map((group) => (
                <label key={group.groupId} className="checkbox-label" style={{ display: 'block', marginBottom: '0.45rem' }}>
                  <input
                    type="checkbox"
                    checked={!!groupMap[group.groupId]}
                    onChange={() => toggle(group.groupId)}
                  />
                  <strong>{group.name}</strong>
                  <span>{group.type}</span>
                </label>
              ))}
            </div>
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Mapping'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
